import ErrorPopup from "./error-popup";
import HttpClient from "./http-client";
import { stringLocalizer } from "./main";
import MagnificPopup, { IPopup, PopupOptions } from "./popup";

export interface QueryParameter {
    key: string;
    value: string | number;
}

let eventListeners = new Map<HTMLElement, string>();

export const isVisible = (element: HTMLElement): boolean => element && element.offsetWidth > 0 && element.offsetHeight > 0;

export const hide = (element: HTMLElement): void => { element.style.display = 'none' };

export const show = (element: HTMLElement): void => { element.style.display = 'block' };

export const addClass = (element: HTMLElement, className: string): void => { if (!element.classList.contains(className)) element.classList.add(className); }

export const removeClass = (element: HTMLElement, className: string): void => { if (element.classList.contains(className)) element.classList.remove(className); }

export const disable = (element: HTMLElement): void => { if (!element.hasAttribute('disabled')) element.setAttribute('disabled', 'disabled') }
export const enable = (element: HTMLElement): void => { if (element.hasAttribute('disabled')) element.removeAttribute('disabled') }

export const setReadonly = (element: HTMLElement): void => { if (!element.hasAttribute('readonly')) element.setAttribute('readonly', '') }
export const unsetReadonly = (element: HTMLElement): void => { if (element.hasAttribute('readonly')) element.removeAttribute('readonly') }

export function includes<T>(array: T[], item: T): boolean {
    return array.indexOf(item) >= 0;
}

export async function asyncForEach<T>(array: T[], callback: (item: T) => Promise<void>) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index]);
    }
}

export const toggleClass = (element: HTMLElement, className: string): void => {
    if (element.classList.contains(className)) {
        element.classList.remove(className);
    } else {
        element.classList.add(className);
    }
}

export function convertToHtmlElement(htmlString: string): HTMLElement {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div.firstElementChild as HTMLElement;
}

export function debounce(func: () => void, delay: number, immediate: boolean) {
    let timeout;
    return function () {
        const context = this, args = arguments;
        const later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, delay);
        if (callNow) func.apply(context, args);
    };
}

export function getBaseUrl(): string {
    return `${window.location.protocol}//${window.location.host}/`;
}

export function getApiUrl(): string {
    return `${getBaseUrl()}api`;
}

function removeGetParameters(url: string): string {
    return url.split('?')[0];
}

function removeAnchor(url: string): string {
    return url.split('#')[0];
}

export function getUrlWithoutQueryString(): string {
    let url = window.location.href;
    url = removeGetParameters(url);
    url = removeAnchor(url);
    return url;
}

export function getQueryString(): string {
    const urlSplit = window.location.href.split('?');
    return urlSplit.length === 2 ? urlSplit[1] : '';
}

export function getQueryParameters(): QueryParameter[] {
    const queryString = getQueryString();
    if (!queryString)
        return [];

    return queryString
        .split('&')
        .map(param => {
            const split = param.split('=');
            return { key: split[0], value: split[0] } as QueryParameter
        });
}

export function addLeadingSlash(url: string): string {
    if (!url || url[0] === '/') {
        return url;
    }
    return `/${url}`;
}

export function removeLeadingSlash(url: string): string {
    if (!url || url[0] !== '/') {
        return url;
    }
    return url.substring(1);
}

export function getCurrentLanguageId(): string {
    const languageAwareElement = document.querySelector('[data-current-language-id]') as HTMLElement;
    return languageAwareElement?.dataset.currentLanguageId;
}

function buildQueryParameter(queryParameter: QueryParameter): string {
    return `${queryParameter.key}=${queryParameter.value}`;
}

export function buildQueryParameters(queryParameters: QueryParameter[]): string {
    if (!queryParameters) {
        return '';
    }

    const queryParametersWithValue = queryParameters.filter(p => p.value);
    const concatenatedParametes = queryParametersWithValue.map(i => buildQueryParameter(i));
    const parameters = concatenatedParametes.join('&');

    return parameters;
}

export function getPopup(options: PopupOptions = null): IPopup {
    return new MagnificPopup(options);
}

export function displayError(message: string, title: string = null) {
    new ErrorPopup().display(message, title);
}

export async function openInPopup(url: string, options: PopupOptions): Promise<IPopup> {
    const popup = getPopup(options);
    const response = await new HttpClient().fetch(encodeURI(url));
    const content = await response.text();
    popup.open(content);
    return popup;
}

export async function openInPopupWithPlaceholder(url: string, options: PopupOptions): Promise<IPopup> {
    const popup = getPopup(options);
    await popup.openAndLoadAsync(async () => {
        const response = await new HttpClient().fetch(encodeURI(url));
        return await response.text();
    });
    return popup;
}

export async function openComponentInPopup(viewComponent: string, queryParams: QueryParameter[] = null, options: PopupOptions, usePlaceholder = false): Promise<IPopup> {
    if (!queryParams) {
        queryParams = [];
    }

    queryParams.push({ key: 'viewComponent', value: viewComponent });

    const url = `${getUrlWithoutQueryString()}?${buildQueryParameters(queryParams)}`;
    return usePlaceholder
        ? await openInPopupWithPlaceholder(url, options)
        : await openInPopup(url, options);
}

export function extractQueryStringFromCurrentUrl(): string {
    let queryParameters = '';
    const currentUrl = window.location.href.split('?');
    if (currentUrl.length > 1) {
        queryParameters = currentUrl[1];
    }

    return queryParameters;
}

export async function loadAsyncComponent(e: Element, optionalQueryParams: QueryParameter[] = null): Promise<HTMLElement> {
    const element = e as HTMLElement;
    const componentName = element.dataset.component;
    const id = element.dataset.componentId;

    let queryParameters = extractQueryStringFromCurrentUrl();
    if (optionalQueryParams != null && optionalQueryParams.length > 0) {
        const params = buildQueryParameters(optionalQueryParams);
        queryParameters = (queryParameters ? `${queryParameters}&${params}` : `${params}`);
    }

    const newQuery = (queryParameters ? `${queryParameters}&` : '') + `viewComponent=${componentName}&componentId=${id}&ajax=true`;

    const response = await new HttpClient().fetch(`${getUrlWithoutQueryString()}?${newQuery}`);
    const content = await response.text();
    const resultElement = convertToHtmlElement(content);

    if (resultElement) {
        element.replaceWith(resultElement);
        return resultElement;
    }

    element.innerHTML = content;
    return element;
}

export function localize(name: string): string {
    return stringLocalizer.get(name);
}

export function getComponentPart(element: HTMLElement, componentPartName: string): HTMLElement {
    return element.querySelector(`[data-component-part="${componentPartName}"]`) as HTMLElement;
}
export function getComponentParts(element: HTMLElement, componentPartName: string): HTMLElement[] {
    return [...element.querySelectorAll(`[data-component-part="${componentPartName}"]`)]
        .map(e => e as HTMLElement);
}
export function getComponent(element: HTMLElement, componentName: string) {
    return element.querySelector(`[data-component="${componentName}"]`) as HTMLElement;
}

export function getComponentById(element: HTMLElement, componentId: string) {
    return element.querySelector(`[data-component-id="${componentId}"]`) as HTMLElement;
}

export function addEventListenerIfNotExists(element: HTMLElement, event: string, listener: (this: HTMLElement, ev: Event) => any) {
    if (!eventListeners.has(element) || eventListeners.get(element) != event) {
        element.addEventListener(event, listener);
        eventListeners.set(element, event);
    }
}

export function getAllowedCookies() {
    const allowed = getCookie('AllowedCookies');
    return allowed.split('|');
}

export function getCookie(cookieName) {
    const name = cookieName + '=';
    const decodedCookies = decodeURIComponent(document.cookie);
    const cookiesArray = decodedCookies.split(';');

    for (let i = 0; i < cookiesArray.length; i++) {
        let cookie = cookiesArray[i];
        while (cookie.charAt(0) === ' ') {
            cookie = cookie.substring(1);
        }
        if (cookie.indexOf(name) === 0) {
            return cookie.substring(name.length, cookie.length);
        }
    }
    return '';
}
